import React, { useEffect, useState } from 'react';
import { notificationApi, notificationWorker, notificationsSupported } from '../utils/newsNotifications';
import { ControlIcon, InfoPopover } from './CompactControls';

const UNITS = { min: 1, sat: 60, dan: 1440 };
const MAX_INTERVAL_MINUTES = 7 * 24 * 60;
const intervalFields = minutes => {
  const unit = minutes % 1440 === 0 ? 'dan' : minutes % 60 === 0 ? 'sat' : 'min';
  return { amount: minutes / UNITS[unit], unit };
};

export default function FilterNotifications({ filter, subscription, onChanged, onStatusChange, statusId }) {
  const [duration, setDuration] = useState(() => intervalFields(subscription?.interval_minutes || 15));
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { onStatusChange?.(status); }, [status, onStatusChange]);
  useEffect(() => {
    const minutes = subscription?.interval_minutes || 15;
    setDuration(current => minutes % UNITS[current.unit] === 0
      ? { ...current, amount: minutes / UNITS[current.unit] } : intervalFields(minutes));
  }, [subscription?.id, subscription?.interval_minutes]);
  const toggle = async () => {
    setBusy(true);
    setStatus('');
    try {
      if (subscription) {
        await notificationApi(`/${subscription.id}`, 'DELETE');
      } else {
        if (!notificationsSupported()) throw new Error('Ovaj uređaj ne podržava obavijesti.');
        if (!window.jaosveNative && await Notification.requestPermission() !== 'granted') throw new Error('Dozvolite obavijesti za ovu stranicu u postavkama preglednika.');
        await notificationWorker();
        await notificationApi('', 'POST', { saved_filter_id: filter.id, interval_minutes: Number(duration.amount) * UNITS[duration.unit] });
      }
      await onChanged();
      window.dispatchEvent(new Event('news-notifications-changed'));
    } catch (error) { setStatus(error.message); }
    finally { setBusy(false); }
  };
  const saveInterval = async (next = duration) => {
    const value = Number(next.amount);
    const interval = Math.min(MAX_INTERVAL_MINUTES, Math.max(15, value * UNITS[next.unit]));
    if (next.amount === '' || !Number.isFinite(value) || !Number.isInteger(value) || value <= 0) {
      setStatus('Unesite pozitivan cijeli broj. Interval mora biti između 15 minuta i 7 dana.');
      return;
    }
    setDuration({ ...next, amount: interval / UNITS[next.unit] });
    setStatus('');
    if (!subscription || interval === subscription.interval_minutes) return;
    setBusy(true);
    try {
      await notificationApi(`/${subscription.id}`, 'PUT', { interval_minutes: interval });
      await onChanged();
    } catch (error) { setStatus(error.message); }
    finally { setBusy(false); }
  };
  return <div className="saved-filter-notification">
    <div className="saved-filter-notification-controls">
      <button type="button" role="switch" aria-checked={Boolean(subscription)} aria-label={`Obavijesti za filter ${filter.name}`} title={subscription ? 'Isključi obavijesti' : 'Uključi obavijesti'}
        className={`notification-bell ${subscription ? 'is-on' : ''}`} disabled={busy || !filter.id} onClick={toggle}>
        <ControlIcon name="bell" />
      </button>
      <InfoPopover title="Obavijesti za spašeni filter">
        <p>Zvonce uključuje obavijesti samo za nove članke ovog filtera. Odaberite broj i jedinicu: min, sat ili dan. Interval je od 15 minuta do 7 dana; kraći interval automatski se postavlja na 15 minuta. Najviše možete unijeti 7 dana, 168 sati ili 10080 minuta; veći broj automatski se ograničava. Promjena se sprema izlaskom iz polja ili izborom jedinice.</p>
        <p>{window.jaosveNative ? 'Potrebna je Android dozvola za obavijesti. Obavijesti stižu i kada je aplikacija zatvorena.' : 'Potrebna je dozvola preglednika i otvorena aplikacija.'} Ako nema novih vijesti, nema obavijesti.</p>
        <p>Obavijest zatvorite dugmetom Zatvori ili otvorite vijesti klikom na nju.</p>
        <p>Klik prikazuje nove vijesti iz te i prethodnih neotvorenih obavijesti istog filtera.</p>
      </InfoPopover>
      {subscription && <div className="notification-minutes" role="group" aria-label={`Interval obavijesti za filter ${filter.name}`}
        onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) saveInterval(); }}>
        <input type="number" aria-describedby={status ? statusId : undefined} aria-label={`Broj za interval filtera ${filter.name}`} min={duration.unit === 'min' ? 15 : 1} max={MAX_INTERVAL_MINUTES / UNITS[duration.unit]} step="1" value={duration.amount} disabled={busy} onChange={event => {
          const amount = event.target.value;
          const maximum = MAX_INTERVAL_MINUTES / UNITS[duration.unit];
          setDuration({ ...duration, amount: amount !== '' && Number(amount) > maximum ? maximum : amount });
        }} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); } }} />
        <select aria-describedby={status ? statusId : undefined} aria-label={`Jedinica intervala za filter ${filter.name}`} value={duration.unit} disabled={busy}
          onChange={event => { const next = { ...duration, unit: event.target.value }; setDuration(next); saveInterval(next); }}>
          <option value="min">min</option><option value="sat">sat</option><option value="dan">dan</option>
        </select>
      </div>}

    </div>
    {status && !onStatusChange && <p className="notification-error" role="alert">{status}</p>}
  </div>;
}
